import {fromJS, Map} from 'immutable';
import {callAction, Store} from 'client/framework';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import sessionStore from 'client/Session/SessionStore';
import {history} from 'client/history';
import {resetActingAsCache, setActingAsRefusalHandler, setActingAsSource} from 'lib/actingAsHeader';

import masqueradeActions from './Masquerade.actions';
import {
  clearMasqueradeState,
  readMasqueradeState,
  writeMasqueradeState
} from './Masquerade.storage';

import type {SchoolModelV5} from 'resources/augmented/School/SchoolModel.v5';
import type {TeacherModelV1} from 'resources/augmented/Teacher/TeacherModel.v1';

export class MasqueradeStore extends Store {
  /** The header value cached results were last reset for. */
  private resetForActingAsId: string | null = null;

  constructor(name: string) {
    super(name);
    this.initialData = fromJS({
      enabled: false,
      user: {},
      school: {},
      origin: null,
      actingAsHeader: false
    });
    this.setInitialData(this.initialData);
    this.rehydrateFromStorage();
    this.handle(masqueradeActions.ENABLE_MASQUERADING, this.enableMasquerading);
    this.handle(masqueradeActions.DISABLE_MASQUERADING, this.disableMasquerading);
    setActingAsSource(() => this.getActingAsId());
    setActingAsRefusalHandler((refusedId) => {
      if (refusedId !== null && refusedId === this.getActingAsId()) {
        callAction(masqueradeActions.DISABLE_MASQUERADING);
      }
    });
  }

  /**
   * Restore masquerade state persisted before a page refresh. Only ids are
   * stored; the panel re-fetches the teacher/school resources by id. `adminId`
   * is kept so `isMasquerading` can reject state owned by a different account.
   */
  rehydrateFromStorage() {
    const persisted = readMasqueradeState();
    if (!persisted) {
      return;
    }
    this.writeData(
      Map({
        enabled: true,
        adminId: persisted.adminId,
        user: Map({id: persisted.userId}),
        school: Map({id: persisted.schoolId}),
        origin: persisted.origin,
        actingAsHeader: persisted.actingAsHeader === true
      })
    );
    this.resetForActingAsId = this.getActingAsId();
  }

  /**
   * Starts a masquerade. `actingAsHeader` is the admin's `acting_as_header` answer, read before
   * the masquerade starts (see `startMasquerade`) so the header is right from the first request.
   */
  enableMasquerading({
    user,
    school,
    origin,
    actingAsHeader = false
  }: {
    user: Map<string, unknown>;
    school: Map<string, unknown>;
    origin: string;
    actingAsHeader?: boolean;
  }) {
    /**
     * To enable masquerading we now require both a user AND school, this ensures
     * we're accounting for `super` users and school administrators of multiple
     * schools... this also accounts for the fact the the `school` on the user
     * can be different than the school the teacher is approved for.
     */
    if (!user || !school) {
      throw Error('An error was encountered while attempting to masquerade.');
    }
    const adminId = sessionStore.getUserId();
    const update = Map({
      user,
      school,
      enabled: true,
      adminId,
      origin: origin || null,
      actingAsHeader
    });
    this.writeData((state: Map<string, unknown>) => state.merge(update));
    writeMasqueradeState({
      adminId: adminId as string,
      userId: this.readData(['user', 'id']),
      schoolId: this.getSchoolId() as string,
      origin: this.readData('origin'),
      actingAsHeader
    });
    void this.resetWhenActingAsChanges();
  }

  disableMasquerading(options: {preventRedirect: boolean} = {preventRedirect: false}) {
    const origin = this.readData('origin');
    clearMasqueradeState();
    this.writeData(this.initialData);
    void this.resetWhenActingAsChanges();
    if (origin && options.preventRedirect === false) {
      setTimeout(() => {
        history.replace(origin, null);
      }, 0);
    }
  }

  isMasquerading(): boolean {
    if (this.readData('enabled') !== true) {
      return false;
    }
    /**
     * Persisted masquerade is bound to the admin who started it. Once the real
     * session user is known, refuse to honor a masquerade owned by someone else
     * (stale tab, re-login as a different account, forged storage). While the
     * session is still resolving `getUserId()` is null, so we don't block —
     * that's the normal refresh-rehydrate window.
     */
    const owner = this.readData('adminId');
    const currentUserId = sessionStore.getUserId();
    if (owner && currentUserId && owner !== currentUserId) {
      return false;
    }
    return true;
  }

  /**
   * Returns a `user.id` based on the masquerade state (falling back to the session store).
   *
   * @returns {string|null} If the session is masquerading as a user, it'll return the masquerade `user.id`, if not it'll return `sessionStore.getUserId()`
   */
  getUserIdByMasqueradeState(): string | null | undefined {
    return this.isMasquerading() ? this.readData(['user', 'id']) : sessionStore.getUserId();
  }

  /**
   * The teacher to name in the `Albert-Acting-As` header: set only while masquerading, for an admin
   * with the `acting_as_header` flag. Without it, masquerade relies on `asEducatorId` as before.
   */
  getActingAsId(): string | null {
    return this.isMasquerading() && this.readData('actingAsHeader') === true
      ? this.readData(['user', 'id'])
      : null;
  }

  /**
   * Drops cached results whenever the header's value changes, since a result cached for one
   * identity can't be served to the other once queries stop naming the teacher themselves. While
   * the header is off, queries pass `asEducatorId` and are cached apart already, so nothing resets.
   * A masquerade restored after a page refresh starts with an empty cache, so it counts as reset.
   */
  async resetWhenActingAsChanges(): Promise<void> {
    const actingAsId = this.getActingAsId();
    if (actingAsId === this.resetForActingAsId) {
      return;
    }
    this.resetForActingAsId = actingAsId;
    try {
      await resetActingAsCache();
    } catch (error) {
      // Apollo clears the cache before refetching, so a rejection means a refetch failed, not the reset.
      logger.error('A query failed to refetch after a masquerade change: %o', error);
    }
  }

  getUserQuery(): QueryBuilder {
    return resource('teacher_v1').mandarkEndpoint(['teachers_v1', this.readData(['user', 'id'])]);
  }

  getUser(): TeacherModelV1 | Map<string, unknown> {
    return this.getUserQuery().getResource();
  }

  getSchoolQuery(): QueryBuilder {
    return resource('school_v5').mandarkEndpoint(['schools_v5', this.getSchoolId()]);
  }

  getSchool(): SchoolModelV5 {
    return this.getSchoolQuery().getResource();
  }

  getSchoolId(): string | null {
    return this.readData(['school', 'id']);
  }
}

export default new MasqueradeStore('MasqueradeStore');
